<template>
	<v-dialog v-model="dialog" max-width="800px" persistent>
		<v-card class="text-xs-left">
			<v-toolbar flat color="indigo" dark dense>
				<v-toolbar-title class="headline">Certification</v-toolbar-title>
				<v-spacer></v-spacer>

				<v-menu offset-y :close-on-content-click="false">
					<template v-slot:activator="{ on, attrs }">
						<v-btn icon v-bind="attrs" v-on="on" class="mx-0">
							<v-icon>mdi-dots-horizontal</v-icon>
						</v-btn>
					</template>
					<v-list>
						<v-list-tile @click="onDeleteCertificationClick">
							<v-icon>mdi-delete</v-icon>
							<v-list-tile-title class="ml-2">Delete Certification</v-list-tile-title>
						</v-list-tile>
					</v-list>
				</v-menu>
				<v-btn icon @click="closeDialog">
					<v-icon>close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-card-text>
				<v-layout row wrap>
					<!-- Left Panel -->
					<v-flex xs12 sm6>
						<v-card flat>
							<v-card-text>
								<div class="mb-3">
									<div class="caption grey--text">Certification</div>
									<div class="font-weight-bold">
                                        {{ certificationName }}
									</div>
								</div>

								<div class="mb-3">
									<div class="caption grey--text">Employee</div>
									<div class="font-weight-medium">
                                        <v-avatar style="margin-top:5px; margin-bottom:5px;" :size="32" color="grey lighten-4" class="mr-1">
											<img :src="getAvatar(certification.employeeId)" alt="avatar" class="avatar-img" />
										</v-avatar>
										<router-link :to="{ name: 'employee', params: { id: certification.employeeId } }">
											{{ certification.employeeName }}
										</router-link>
									</div>
								</div>

								<div class="mb-3">
									<div class="caption grey--text">Expiry</div>
									<div v-if="!isEditing" class="font-weight-medium">
										<AppDate :date="certification.expiryDate" null-date="N/A" show-date />
										<v-chip color="error" small dark v-if="isExpired(certification.expiryDate)" class="ml-2">Expired</v-chip>
										<v-chip color="warning" small dark v-if="isExpiring(certification.expiryDate)" class="ml-2">Expiring Soon</v-chip>
										<div v-if="loggedInEmployeeIsAdminOrManager && canExpire">
											<a href="#" class="caption" @click.prevent="onEditExpiryDateClick">Edit Expiry Date</a>
										</div>
									</div>

									<div v-else class="mt-1">
										<DateInput v-model="editedExpiryDate" label="Expiry Date" class="mr-2" />
										<v-btn small color="primary" @click="saveExpiry">Save</v-btn>
										<v-btn small @click="cancelEdit">Cancel</v-btn>
									</div>
								</div>
							</v-card-text>
						</v-card>
					</v-flex>

					<!-- Right Panel: Files -->
					<v-flex xs12 sm6>
						<v-card class="mt-3">
							<v-toolbar flat dense>
								<v-toolbar-title class="subheading"> <v-icon small class="mr-1">mdi-paperclip</v-icon>Files </v-toolbar-title>
								<v-spacer></v-spacer>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn icon @click="showUploadFileDialog = true" v-on="on">
											<v-icon>mdi-plus</v-icon>
										</v-btn>
									</template>
									<span>Add File</span>
								</v-tooltip>
							</v-toolbar>

							<v-card-text>
								<v-list dense v-if="certification.files && certification.files.length">
									<v-list-tile v-for="file in certification.files" :key="file.id">
										<v-list-tile-avatar>
											<v-icon>mdi-file-document-outline</v-icon>
										</v-list-tile-avatar>
										<v-list-tile-content>
											<v-list-tile-title>{{ file.fileName }}</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<v-tooltip bottom>
												<template v-slot:activator="{ on }">
													<v-btn icon @click="onDownloadFileClick(file)" v-on="on">
														<v-icon>get_app</v-icon>
													</v-btn>
												</template>
												<span>Download</span>
											</v-tooltip>
										</v-list-tile-action>
										<v-list-tile-action>
											<v-tooltip bottom>
												<template v-slot:activator="{ on }">
													<v-btn icon @click.stop="onDeleteFileClick(file)" v-on="on">
														<v-icon>delete</v-icon>
													</v-btn>
												</template>
												<span>Delete File</span>
											</v-tooltip>
										</v-list-tile-action>
									</v-list-tile>
								</v-list>
								<div v-else class="grey--text">There are currently no files</div>
							</v-card-text>
						</v-card>
						<v-card flat class="grey lighten-4 mt-3" v-if="canExpire">
							<v-card-text>
								<div class="grey--text text--darken-2 mb-2"><v-icon small class="mr-1">mdi-calendar-clock</v-icon>Expiry Reminders</div>

								<div class="mb-2">
									<div class="caption grey--text">Last Reminder</div>
									<div class="font-weight-medium">
										<AppDate :date="certification.lastReminderUtc" nullDate="N/A" />
									</div>
								</div>

								<div class="mb-2">
									<div class="caption grey--text">Next Reminder</div>
									<div class="font-weight-medium">
										<AppDate :date="certification.nextReminderUtc" nullDate="N/A" />
									</div>
								</div>

								<div v-if="loggedInEmployeeIsAdminOrManager && hasExpiry && isExpiring(certification.expiryDate)">
									<div class="caption grey--text">Manual Reminder</div>
									<a href="#" class="caption" @click.prevent="handleReminder(certification)">Send to Employee</a>
								</div>
							</v-card-text>
						</v-card>
					</v-flex>
				</v-layout>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn flat @click="closeDialog">Close</v-btn>
			</v-card-actions>
		</v-card>

		<!-- Dialogs -->
		<file-upload v-if="showUploadFileDialog" :dialog="showUploadFileDialog" :loading="isUploadingFile" title="Upload Certification File" @onSecondaryAction="showUploadFileDialog = false" @onPrimaryAction="handleFileUpload" />
		<confirmation-dialog :dialog="showDeleteFileDialog" title="Confirm Delete File" text="Are you sure you want to delete this file?" :isConfirming="isDeletingFile" @onConfirm="handleDeleteFile" @onCancel="showDeleteFileDialog = false" />
		<confirmation-dialog
			:dialog="showDeleteCertDialog"
			title="Confirm Delete Certification"
			text="Are you sure you want to delete this certification?"
			:isConfirming="isDeletingCertification"
			@onConfirm="handleDeleteCertification"
			@onCancel="showDeleteCertDialog = false"
		/>
	</v-dialog>
</template>

<script>
	import avatarMixin from '@/mixins/avatarMixin';
	import certificationsMixin from '@/mixins/certificationsMixin';
	import DateInput from '@/components/common/DateInput.vue';
	import { mapActions, mapGetters } from 'vuex';
	import moment from 'moment';
	import FileUpload from '@components/FileUpload.vue';

	export default {
		props: {
			certification: {
				type: Object,
				required: true
			}
		},
		components: { DateInput, FileUpload },
		mixins: [certificationsMixin, avatarMixin],
		data() {
			return {
				dialog: true,
				isEditing: false,
				menu: false,
				editedExpiryDate: this.certification.expiryDate || null,
				today: moment().format('YYYY-MM-DD'),
				showUploadFileDialog: false,
				showDeleteFileDialog: false,
				deletingFile: undefined,
				showDeleteCertDialog: false
			};
		},
		computed: {
			...mapGetters({
				loggedInEmployeeIsAdminOrManager: 'loggedInEmployeeIsAdminOrManager',
				isUploadingFile: 'certifications/isUploadingFile',
				isDeletingFile: 'certifications/isDeletingFile',
				isDeletingCertification: 'certifications/isDeletingCertification'
			}),
			hasExpiry() {
				return !!this.certification.expiryDate;
			},
			canExpire() {
				return this.certification.typeConfiguration.hasExpiry;
			},
			certificationName() {
				return `${this.certification.name}`;
			}
		},
		methods: {
			...mapActions({
				downloadFile: 'certifications/download',
				deleteFile: 'certifications/deleteFile',
				handleReminder: 'certificationActions/handleReminder',
				updateExpiry: 'certifications/updateExpiry',
				uploadFiles: 'certifications/uploadFiles',
				deleteCertification: 'certifications/delete'
			}),
			async saveExpiry() {
				this.certification.expiryDate = this.editedExpiryDate;
				this.isEditing = false;
				const updateResult = await this.updateExpiry({
					employeeId: this.certification.employeeId,
					expiryDate: this.editedExpiryDate,
					id: this.certification.id
				});
				if (updateResult.isSuccess) {
					this.$emit('onCertificationUpdated', updateResult.data);
				}
			},
			cancelEdit() {
				this.isEditing = false;
				this.editedExpiryDate = this.certification.expiryDate;
			},
			closeDialog() {
				this.$emit('closeDialog');
			},
			async onDownloadFileClick(file) {
				await this.downloadFile({
					certificationId: this.certification.id,
					fileId: file.id
				});
			},
			onEditExpiryDateClick() {
				this.editedExpiryDate = this.certification.expiryDate ? this.certification.expiryDate.substr(0, 10) : null;
				this.isEditing = true;
			},
			async handleFileUpload(uploadFormData) {
				uploadFormData.append('employeeId', this.certification.employeeId);

				const result = await this.uploadFiles({ certificationId: this.certification.id, document: uploadFormData });

				if (result.isSuccess) {
					this.showUploadFileDialog = false;
				}
			},
			onDeleteFileClick(file) {
				this.showDeleteFileDialog = true;
				this.deletingFile = file;
			},
			async handleDeleteFile() {
				const response = await this.deleteFile({ certificationId: this.certification.id, fileId: this.deletingFile.id });

				if (response.isSuccess) {
					this.deletingFile = null;
					this.showDeleteFileDialog = false;
				}
			},
			onDeleteCertificationClick() {
				this.showDeleteCertDialog = true;
			},
			async handleDeleteCertification() {
				const response = await this.deleteCertification(this.certification.id);

				if (response.isSuccess) {
					this.showDeleteCertDialog = false;
					this.closeDialog();
				}
			}
		}
	};
</script>

<style scoped>
	.preview-thumb {
		width: 36px;
		height: 36px;
		object-fit: cover;
		border-radius: 4px;
		border: 1px solid #ccc;
	}
</style>
